<template>
  <div class="level">
    <div class="level__wrapper">
      <div class="level__title-wrapper">
        <p class="level__title">{{ level.description }}</p>
      </div>
      <div class="level__details-wrapper">
        <Actions
          v-if="isUser"
          :hasWatched="hasWatched"
          :isWatching="isWatching"
          :videoTime="$duration.durationOn(levelVideo.duration)"
          :hasQuiz="levelQuiz"
          :hasPassedQuiz="hasPassedQuiz"
          :isTakingQuiz="isTakingQuiz"
          :isLoading="isLoading"
          @toggleVideo="toggleVideo"
          @toggleQuiz="toggleQuiz"
        />
        <div v-if="isUser" class="level__resume">
          <button
            v-if="!isWatching"
            @click="toggleContent()"
            :class="
              hasWatched ? 'btn__large' : 'btn__large btn__large--alternate'
            "
          >
            {{ levelStateText }}
            <i
              v-if="hasWatched"
              class="btn__large-icon btn__large-icon--alt material-icons"
              >replay</i
            >
            <PlayButtonIcon class="btn__large-icon" v-else />
          </button>
          <button v-else @click="toggleVideo()" class="btn__large">
            Stop
            <i class="btn__large-icon material-icons">stop</i>
          </button>
        </div>
        <div v-else class="level__resume">
          <router-link
            class="level__login-link btn__action"
            :to="{ name: 'login' }"
          >
            Login
            <i class="btn__action-icon material-icons">person</i>
          </router-link>
        </div>
      </div>
    </div>
    <div class="level__content-wrapper">
      <Content
        :video="levelVideo"
        :isWatching="isWatching"
        :hasWatched="hasWatched"
        :quizId="levelQuiz"
        :isTakingQuiz="isTakingQuiz"
        :hasPassedQuiz="hasPassedQuiz"
        :levelTitle="level.description"
        :nextVideo="nextVideo"
        :isLoading="isLoading"
        @isLoading="setLoader"
        @toggleVideo="toggleVideo"
        @toggleWatched="toggleWatched"
        @advanceToNextVideo="advanceToNextVideo"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Actions from '@/components/course/content/level/Actions.vue'
import Content from '@/components/course/content/level/Content.vue'
import PlayButtonIcon from '@/assets/icons/course/play-icon.svg'

export default {
  name: 'Level',
  data() {
    return {
      isLoading: false,
    }
  },
  components: {
    Actions,
    Content,
    PlayButtonIcon: { ...PlayButtonIcon, name: 'PlayButtonIcon' },
  },
  props: {
    level: {
      type: Object,
      required: true,
    },
    isUser: {
      type: Boolean,
      required: true,
    },
    userId: {
      type: String,
      required: false,
    },
    courseId: {
      type: String,
      required: true,
    },
    progress: {
      type: [Array, Object],
      required: false,
    },
    videos: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      playingVideoId: 'content/playingVideoId',
      currentQuizId: 'content/currentQuizId',
    }),
    levelVideo() {
      const { videos } = this.level
      return videos[0]
    },
    levelQuiz() {
      const { quizzes } = this.level
      if (quizzes && quizzes.length) {
        return this.level.quizzes[0]
      }
      return ''
    },
    hasWatched() {
      if (this.progress && this.progress.videos) {
        return !!this.progress.videos.find(
          video => video.id === this.levelVideo.id
        )
      }
      return false
    },
    hasPassedQuiz() {
      if (this.progress && this.progress.quiz_attempts) {
        const { quiz_attempts: quizAttempts } = this.progress
        const currentQuiz = quizAttempts.find(
          quiz => quiz.id === this.levelQuiz
        )
        return currentQuiz ? currentQuiz.has_passed : false
      }
      return false
    },
    isWatching() {
      const { id: levelVideoId } = this.levelVideo
      return levelVideoId === this.playingVideoId
    },
    isTakingQuiz() {
      if (this.levelQuiz) {
        return this.levelQuiz === this.currentQuizId
      }
      return false
    },
    nextVideo() {
      const position = this.videos.findIndex(
        currentVideo => currentVideo.id === this.levelVideo.id
      )
      return this.videos[position + 1]
    },
    nextContent() {
      if (this.hasWatched && this.levelQuiz && !this.hasPassedQuiz) {
        return this.levelQuiz
      }
      return this.nextVideo
    },
    levelStateText() {
      if (!this.hasWatched) {
        return 'Play'
      }
      if (this.hasWatched && this.levelQuiz && !this.hasPassedQuiz) {
        return 'Resume'
      }
      return 'Replay'
    },
  },
  methods: {
    toggleVideo() {
      const { id } = this.levelVideo
      if (this.playingVideoId === id) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setPlayingVideoTime', 0)
        this.isLoading = false
      } else {
        this.$store.dispatch('content/setPlayingVideoTime', 0)
        this.$store.dispatch('content/setPlayingVideoId', id)
      }
    },
    toggleQuiz() {
      if (this.currentQuizId === this.levelQuiz) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', '')
      } else {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', this.levelQuiz)
      }
    },
    toggleContent() {
      if (!this.levelQuiz) {
        this.toggleVideo()
      } else if (this.hasWatched && this.levelQuiz && !this.hasPassedQuiz) {
        this.toggleQuiz()
      } else {
        this.toggleVideo()
      }
    },
    async toggleWatched() {
      const { userId, courseId } = this
      const { id: vidId } = this.levelVideo
      const userAndVideoId = { userId, vidId }
      if (this.hasWatched) {
        await this.$store.dispatch('user/unmarkVideoAsWatched', userAndVideoId)
      } else {
        await this.$store.dispatch('user/markVideoAsWatched', userAndVideoId)
      }
      const courseIdAndUserId = { courseId, userId }
      await this.$store.dispatch(
        'user/getUserCourseProgress',
        courseIdAndUserId
      )
    },
    advanceToNextVideo() {
      if (!this.hasPassedQuiz && this.levelQuiz) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', this.levelQuiz)
        return
      }
      if (this.nextVideo) {
        const { id } = this.nextVideo
        this.$store.dispatch('content/setPlayingVideoId', id)
      } else {
        this.$store.dispatch('content/setPlayingVideoId', '')
      }
    },
    setLoader(state) {
      this.isLoading = state
    },
  },
}
</script>

<style lang="scss" scoped>
.level {
  &__wrapper {
    min-height: 75px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    background-color: var(--light-gray);
    padding: 25px;
    margin: 0;
    @include tablet {
      padding: 10px 25px;
      margin: 0 0 0 15px;
    }
  }
  &__title-wrapper {
    width: 100%;
    display: flex;
    margin: 0 0 20px 0;
    @include tablet {
      margin: 0;
      width: 60%;
    }
  }
  &__title {
    color: var(--white);
    font-size: 18px;
    font-weight: 700;
    font-family: var(--font-bold);
    margin: 0;
    width: 100%;
  }
  &__details-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    @include desktop {
      width: 35%;
    }
  }
  &__resume {
    margin: auto 0 0 auto;
    @include desktop {
      margin: 0 0 0 auto;
    }
  }
  &__video-progress {
    width: 25%;
    @include mobile {
      width: 10%;
    }
    @include tablet {
      width: 40%;
    }
  }
  &__progress {
    display: flex;
    align-items: baseline;
    color: var(--green);
    @include tablet {
      padding: 0 0 0 20px;
    }
    &--uncheck {
      position: relative;
      color: var(--black);
      background-color: var(--green);
      border-radius: 100%;
      font-size: 1.5rem;
      padding: 5px;
      &:hover {
        cursor: pointer;
        background-color: var(--gray);
      }
    }
    &--check {
      font-size: 1.5rem;
      position: relative;
      padding: 5px;
      color: var(--black);
      background-color: var(--gray);
      border-radius: 100%;
      opacity: 0.2;
      &:hover {
        cursor: pointer;
        background-color: var(--green);
        opacity: 1;
      }
    }
  }
  &__video-playback {
    @include tablet {
      width: 30%;
    }
  }
  &__login-link {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--orange);
    max-width: 130px;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>
